import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { buildTheme } from './theme';

type ColorMode = 'light' | 'dark';

interface ColorModeContextValue {
  mode: ColorMode;
  toggleColorMode: () => void;
}

const ColorModeContext = createContext<ColorModeContextValue>({
  mode: 'light',
  toggleColorMode: () => undefined,
});

const STORAGE_KEY = 'am-color-mode';

/**
 * Provides the current light/dark color mode and MUI theme to the app,
 * persisting the user's choice across sessions.
 * @param children - Subtree that should receive the themed context.
 */
export function ColorModeProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<ColorMode>(() => {
    const stored = localStorage.getItem(STORAGE_KEY) as ColorMode | null;
    if (stored) return stored;
    // No stored preference yet — fall back to the OS/browser preference.
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  const value = useMemo<ColorModeContextValue>(
    () => ({
      mode,
      toggleColorMode: () =>
        setMode((prev) => {
          const next = prev === 'light' ? 'dark' : 'light';
          localStorage.setItem(STORAGE_KEY, next);
          return next;
        }),
    }),
    [mode],
  );

  const { i18n } = useTranslation();
  const theme = useMemo(() => buildTheme(mode, i18n.language), [mode, i18n.language]);

  return (
    <ColorModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

/**
 * Accesses the current color mode and a toggle function.
 * @returns The active {@link ColorModeContextValue}.
 */
export function useColorMode() {
  return useContext(ColorModeContext);
}
